import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, endWith, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs'

import {
  AiAssistantAPI,
  AnswerPayload,
} from '@flowx/designer/modules/new-ai/agents/assistant/api/assistant.api'

import { AiAssistantActions } from './assistant.actions'

// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'

@Injectable()
export class AiAssistantEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiAssistantAPI = inject(AiAssistantAPI)

  getAnswer = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAssistantActions.getAnswer),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.isZenMode)
      ),
      map(([{ prompt, appId, appVersion, processDoc }, sessionId, isZenMode]) => [
        {
          question: prompt,
          session_id: sessionId,
          application_id: appId ? appId.toString() : '',
          application_version: appVersion ? appVersion.toString() : '',
          jump_to_process_doc: processDoc,
        } satisfies AnswerPayload,
        isZenMode,
      ]),
      switchMap(([payload, isZenMode]) =>
        isZenMode
          ? this.#aiAssistantAPI.getSSEAnswer(payload as AnswerPayload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiAssistantActions.getAnswerEventStream({ response })),
              catchError(({ status }) =>
                of(
                  AiActions.getAnswerFailure({
                    statusCode: status,
                    errorMsg: null,
                  })
                )
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
          : this.#aiAssistantAPI.getAnswer(payload as AnswerPayload).pipe(
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              map((response) => AiAssistantActions.getAnswerEventStreamOld({ response })),
              catchError(({ status }) =>
                of(
                  AiActions.getAnswerFailure({
                    statusCode: status,
                    errorMsg: null,
                  })
                )
              ),
              endWith(AiActions.getAnswerStreamCompleted())
            )
      )
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAssistantActions.sendFeedback),
        switchMap(({ payload }) => this.#aiAssistantAPI.sendFeedback(payload))
      ),
    { dispatch: false }
  )
}
